import React, { useEffect, useState, useCallback } from "react";
import {
  Truck, Package, RotateCcw, AlertTriangle, Settings2, Loader2, Printer, Ban, RefreshCw,
  Undo2, PauseCircle, PlayCircle, MapPin, DollarSign, CheckCircle2, ChevronDown, ChevronRight,
  ExternalLink, Layers,
} from "lucide-react";
import { toast } from "sonner";
import * as V from "../../lib/vendor";

const money = (c) => `$${((c || 0) / 100).toFixed(2)}`;
const scopeId = (storeCtx) => (storeCtx && storeCtx !== "__org__" ? storeCtx : undefined);

const SUB = [
  { key: "overview", label: "Overview", icon: Truck },
  { key: "shipments", label: "Shipments", icon: Package },
  { key: "returns", label: "Returns", icon: RotateCcw },
  { key: "exceptions", label: "Exceptions", icon: AlertTriangle },
  { key: "settings", label: "Settings", icon: Settings2 },
];

const STATUS_STYLE = {
  PENDING: "bg-amber-100 text-amber-700", LABEL_PURCHASED: "bg-blue-100 text-blue-700",
  SHIPPED: "bg-indigo-100 text-indigo-700", IN_TRANSIT: "bg-indigo-100 text-indigo-700",
  DELIVERED: "bg-green-100 text-green-700", FAILED: "bg-red-100 text-red-600",
  RETURNED_TO_SENDER: "bg-orange-100 text-orange-700", EXCEPTION: "bg-red-100 text-red-600",
  LOST: "bg-red-100 text-red-600", DAMAGED: "bg-red-100 text-red-600",
};

const Card = ({ title, children, action, testid }) => (
  <div className="bg-white border border-neutral-200 rounded-xl p-5 mb-5" data-testid={testid}>
    {title && <div className="flex items-center justify-between mb-4"><h2 className="text-sm font-bold uppercase tracking-wide text-taven-black">{title}</h2>{action}</div>}
    {children}
  </div>
);
const Tile = ({ label, value, testid, tone }) => (
  <div className="bg-white border border-neutral-200 rounded-xl p-4" data-testid={testid}>
    <div className="text-[11px] uppercase tracking-wide text-neutral-400 font-semibold">{label}</div>
    <div className={`mt-1 text-xl font-bold ${tone || "text-taven-black"}`}>{value}</div>
  </div>
);
const Badge = ({ children, cls }) => (
  <span className={`inline-block text-[10px] font-bold uppercase px-2 py-0.5 rounded ${cls || "bg-neutral-100 text-neutral-600"}`}>{children}</span>
);
const Btn = ({ children, onClick, testid, tone = "dark", disabled, icon: Icon }) => {
  const styles = {
    dark: "bg-taven-black text-white hover:bg-taven-orange",
    ghost: "border border-neutral-200 text-neutral-700 hover:bg-neutral-50",
    danger: "border border-red-200 text-red-600 hover:bg-red-50",
  };
  return (
    <button onClick={onClick} disabled={disabled} data-testid={testid}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-bold uppercase tracking-wide rounded-md transition-colors disabled:opacity-40 ${styles[tone]}`}>
      {Icon && <Icon className="w-3.5 h-3.5" />}{children}
    </button>
  );
};

export default function SellerShipping({ store, stores, storeCtx }) {
  const [sub, setSub] = useState("overview");
  const [disabled, setDisabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [dash, setDash] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await V.vShipDashboard(scopeId(storeCtx));
      setDash(d); setDisabled(false);
    } catch (e) {
      if (e?.response?.status === 503) setDisabled(true);
    } finally { setLoading(false); }
  }, [storeCtx]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-taven-gold" /></div>;
  if (disabled) return (
    <Card testid="shipping-disabled">
      <div className="text-center py-10">
        <Truck className="w-9 h-9 mx-auto mb-3 text-taven-gold" />
        <div className="text-sm font-bold text-taven-black">Native Shipping is being prepared</div>
        <div className="text-xs text-neutral-500 mt-1 max-w-sm mx-auto">Label purchasing, tracking and returns unlock for your organization shortly. Your existing fulfillment keeps working meanwhile.</div>
      </div>
    </Card>
  );

  return (
    <div data-testid="seller-shipping">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold text-taven-black">Shipping</h1>
          <p className="text-sm text-neutral-500">{storeCtx === "__org__" ? "All stores" : store?.name} · isolated to your organization</p>
        </div>
      </div>
      <div className="flex gap-1.5 mb-5 overflow-x-auto">
        {SUB.map((s) => (
          <button key={s.key} onClick={() => setSub(s.key)} data-testid={`ship-tab-${s.key}`}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-semibold whitespace-nowrap transition-colors ${sub === s.key ? "bg-taven-black text-white" : "text-neutral-600 hover:bg-neutral-100"}`}>
            <s.icon className="w-4 h-4" />{s.label}
          </button>
        ))}
      </div>

      {sub === "overview" && <Overview dash={dash} />}
      {sub === "shipments" && <Shipments storeCtx={storeCtx} onChange={load} />}
      {sub === "returns" && <Returns storeCtx={storeCtx} />}
      {sub === "exceptions" && <Exceptions storeCtx={storeCtx} />}
      {sub === "settings" && <ProfileSettings store={store} stores={stores} />}
    </div>
  );
}

function Overview({ dash }) {
  if (!dash) return null;
  return (
    <div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        <Tile label="Awaiting Shipment" value={dash.awaiting_shipment} testid="ship-tile-awaiting" tone="text-amber-600" />
        <Tile label="In Transit" value={dash.in_transit} testid="ship-tile-transit" />
        <Tile label="Delivered" value={dash.delivered} testid="ship-tile-delivered" tone="text-green-600" />
        <Tile label="Total Shipments" value={dash.total_shipments} testid="ship-tile-total" />
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        <Tile label="Open Exceptions" value={dash.exceptions} testid="ship-tile-exceptions" tone={dash.exceptions ? "text-red-600" : "text-taven-black"} />
        <Tile label="Returns" value={dash.returns} testid="ship-tile-returns" />
        <Tile label="External Orders" value={dash.external_shipments} testid="ship-tile-external" />
        <Tile label="Shipping Charges (Wallet)" value={money(dash.shipping_cost_cents)} testid="ship-tile-cost" />
      </div>
      <Card title="Carrier Performance" testid="ship-carrier-card">
        {Object.keys(dash.carrier_performance || {}).length === 0
          ? <p className="text-sm text-neutral-400">No labels purchased yet.</p>
          : <div className="flex flex-wrap gap-2">{Object.entries(dash.carrier_performance).map(([k, v]) => <Badge key={k} cls="bg-neutral-100 text-neutral-700">{k}: {v}</Badge>)}</div>}
      </Card>
    </div>
  );
}

function Shipments({ storeCtx, onChange }) {
  const [items, setItems] = useState([]);
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState("awaiting");
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [selected, setSelected] = useState({});
  const [bulk, setBulk] = useState({ weight_grams: 250, length: 20, width: 15, height: 5, country: "US", zip: "" });
  const [bulkBusy, setBulkBusy] = useState(false);
  const [creating, setCreating] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const status = filter === "awaiting" ? undefined : (filter === "all" ? undefined : filter);
      const d = await V.vShipList(scopeId(storeCtx), status);
      let list = d.shipments || [];
      if (filter === "awaiting") list = list.filter((s) => ["PENDING", "LABEL_PURCHASED"].includes(s.status));
      setItems(list);
      const shipSubIds = new Set((d.shipments || []).map((s) => s.vendor_suborder_id));
      try {
        const o = await V.vListOrders(scopeId(storeCtx));
        setOrders((o.suborders || []).filter((so) => !shipSubIds.has(so.suborder_id) && !["DELIVERED", "CANCELLED", "REFUNDED"].includes(so.state)));
      } catch { setOrders([]); }
    } catch (e) { toast.error("Could not load shipments"); }
    finally { setLoading(false); }
  }, [storeCtx, filter]);
  useEffect(() => { load(); }, [load]);

  const refresh = () => { load(); onChange && onChange(); };
  const toggleSel = (id) => setSelected((s) => ({ ...s, [id]: !s[id] }));
  const selectedIds = Object.keys(selected).filter((k) => selected[k]);

  const createShipment = async (so) => {
    setCreating(so.suborder_id);
    try { await V.vShipCreate({ suborder_id: so.suborder_id }); toast.success("Shipment created"); refresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not create shipment"); }
    finally { setCreating(""); }
  };

  const bulkBuy = async () => {
    const ids = selectedIds.filter((id) => (items.find((i) => i.shipment_id === id) || {}).status === "PENDING");
    if (ids.length === 0) return toast.error("Select PENDING shipments to bulk-purchase");
    if (!bulk.zip) return toast.error("Enter a default destination ZIP for bulk purchase");
    setBulkBusy(true);
    let ok = 0, fail = 0;
    const pkg = { weight_grams: Number(bulk.weight_grams), dimensions: { length: Number(bulk.length), width: Number(bulk.width), height: Number(bulk.height) }, destination: { country: bulk.country, zip: bulk.zip } };
    for (const id of ids) {
      try {
        const r = await V.vShipRates(id, pkg);
        const cheapest = (r.rates || []).slice().sort((a, b) => a.cost_cents - b.cost_cents)[0];
        if (!cheapest) { fail++; continue; }
        await V.vShipBuy(id, cheapest.rate_id);
        ok++;
      } catch { fail++; }
    }
    setBulkBusy(false); setSelected({});
    toast.success(`Bulk purchase: ${ok} label(s) bought${fail ? `, ${fail} failed` : ""}`);
    refresh();
  };

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2 mb-4">
        {["awaiting", "all", "SHIPPED", "IN_TRANSIT", "DELIVERED", "EXCEPTION"].map((f) => (
          <button key={f} onClick={() => setFilter(f)} data-testid={`ship-filter-${f}`}
            className={`text-[11px] px-3 py-1.5 rounded-full font-semibold ${filter === f ? "bg-taven-black text-white" : "bg-neutral-100 text-neutral-500"}`}>
            {f === "awaiting" ? "Awaiting Shipment" : f === "all" ? "All" : f.replace(/_/g, " ")}
          </button>
        ))}
        <button onClick={refresh} className="ml-auto text-neutral-400 hover:text-taven-black" data-testid="ship-refresh"><RefreshCw className="w-4 h-4" /></button>
      </div>

      {orders.length > 0 && (
        <Card title={`Orders Awaiting a Shipment (${orders.length})`} testid="awaiting-orders-card">
          <p className="text-[11px] text-neutral-400 mb-3">Create a shipment to buy a label and start tracking. Fulfillment responsibility is resolved automatically.</p>
          <div className="space-y-2" data-testid="awaiting-orders-list">
            {orders.map((so) => (
              <div key={so.suborder_id} className="flex items-center gap-3 border border-neutral-100 rounded-lg p-3" data-testid={`awaiting-order-${so.suborder_id}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-taven-black truncate">#{so.shopify_order_id}</div>
                  <div className="text-[11px] text-neutral-400">{so.items?.length || 0} item(s) · net {money(so.amounts?.seller_net_cents)} · {so.state}</div>
                </div>
                <Btn onClick={() => createShipment(so)} disabled={creating === so.suborder_id} testid={`create-shipment-${so.suborder_id}`} icon={creating === so.suborder_id ? Loader2 : Truck}>
                  {creating === so.suborder_id ? "Creating…" : "Create Shipment"}
                </Btn>
              </div>
            ))}
          </div>
        </Card>
      )}

      {selectedIds.length > 0 && (
        <Card title={`Bulk Label Purchase (${selectedIds.length} selected)`} testid="ship-bulk-card">
          <p className="text-[11px] text-neutral-400 mb-3">Applies one default package + destination to buy the cheapest rate for each selected PENDING shipment.</p>
          <div className="grid grid-cols-2 sm:grid-cols-6 gap-2 mb-3">
            <input data-testid="bulk-weight" type="number" value={bulk.weight_grams} onChange={(e) => setBulk({ ...bulk, weight_grams: e.target.value })} placeholder="Weight (g)" className="binp" />
            <input data-testid="bulk-length" type="number" value={bulk.length} onChange={(e) => setBulk({ ...bulk, length: e.target.value })} placeholder="L (cm)" className="binp" />
            <input data-testid="bulk-width" type="number" value={bulk.width} onChange={(e) => setBulk({ ...bulk, width: e.target.value })} placeholder="W (cm)" className="binp" />
            <input data-testid="bulk-height" type="number" value={bulk.height} onChange={(e) => setBulk({ ...bulk, height: e.target.value })} placeholder="H (cm)" className="binp" />
            <input data-testid="bulk-country" value={bulk.country} onChange={(e) => setBulk({ ...bulk, country: e.target.value })} placeholder="Country" className="binp" />
            <input data-testid="bulk-zip" value={bulk.zip} onChange={(e) => setBulk({ ...bulk, zip: e.target.value })} placeholder="ZIP" className="binp" />
          </div>
          <Btn onClick={bulkBuy} disabled={bulkBusy} testid="bulk-buy-btn" icon={bulkBusy ? Loader2 : Layers}>{bulkBusy ? "Purchasing…" : "Bulk Purchase Labels"}</Btn>
        </Card>
      )}

      <Card testid="ship-list-card">
        {loading ? <div className="py-8 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-taven-gold" /></div> :
          items.length === 0 ? <p className="text-sm text-neutral-400" data-testid="ship-empty">No shipments in this view.</p> :
            <div className="space-y-2" data-testid="ship-list">
              {items.map((s) => (
                <ShipmentRow key={s.shipment_id} s={s} expanded={expanded === s.shipment_id}
                  onExpand={() => setExpanded(expanded === s.shipment_id ? null : s.shipment_id)}
                  selected={!!selected[s.shipment_id]} onSelect={() => toggleSel(s.shipment_id)}
                  onChange={refresh} />
              ))}
            </div>}
      </Card>
      <style>{`.binp{border:1px solid #e5e5e5;border-radius:8px;padding:7px 9px;font-size:12px;width:100%}`}</style>
    </div>
  );
}

function ShipmentRow({ s, expanded, onExpand, selected, onSelect, onChange }) {
  const buyable = ["SELLER_FULFILLED", "TAVEN_COORDINATED", "THIRD_PARTY_LOGISTICS", "WHOLESALE_FREIGHT"].includes(s.fulfillment_responsibility);
  const labeled = s.label_status === "PURCHASED";
  return (
    <div className="border border-neutral-100 rounded-lg" data-testid={`ship-row-${s.shipment_id}`}>
      <div className="flex items-center gap-3 p-3">
        {s.status === "PENDING" && buyable && (
          <input type="checkbox" checked={selected} onChange={onSelect} data-testid={`ship-select-${s.shipment_id}`} className="w-4 h-4 accent-taven-black" />
        )}
        <button onClick={onExpand} className="text-neutral-400" data-testid={`ship-expand-${s.shipment_id}`}>{expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}</button>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-sm text-taven-black truncate">#{s.customer_order_id || s.shipment_id.slice(0, 8)}</div>
          <div className="text-[11px] text-neutral-400">{(s.fulfillment_responsibility || "").replace(/_/g, " ")} · {s.carrier || "no carrier"}{s.tracking_number ? ` · ${s.tracking_number}` : ""}</div>
        </div>
        {s.fulfillment_hold && <Badge cls="bg-orange-100 text-orange-700">On Hold</Badge>}
        {s.label_status && <Badge cls="bg-neutral-100 text-neutral-500">{s.label_status}</Badge>}
        <Badge cls={STATUS_STYLE[s.status] || "bg-neutral-100 text-neutral-600"}>{s.status}</Badge>
      </div>
      {expanded && <ShipmentActions s={s} buyable={buyable} labeled={labeled} onChange={onChange} />}
    </div>
  );
}

function ShipmentActions({ s, buyable, labeled, onChange }) {
  const [pkg, setPkg] = useState({ weight_grams: 250, length: 20, width: 15, height: 5, country: (s.destination?.country || "US"), zip: (s.destination?.zip || "") });
  const [rates, setRates] = useState(s.available_rates || []);
  const [busy, setBusy] = useState("");
  const act = async (key, fn, msg) => {
    setBusy(key);
    try { await fn(); toast.success(msg); onChange(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
    finally { setBusy(""); }
  };
  const getRates = async () => {
    if (!pkg.zip) return toast.error("Destination ZIP required");
    setBusy("rates");
    try {
      const r = await V.vShipRates(s.shipment_id, { weight_grams: Number(pkg.weight_grams), dimensions: { length: Number(pkg.length), width: Number(pkg.width), height: Number(pkg.height) }, destination: { country: pkg.country, zip: pkg.zip } });
      setRates(r.rates || []); toast.success("Rates retrieved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not get rates"); }
    finally { setBusy(""); }
  };

  return (
    <div className="border-t border-neutral-100 p-3 bg-neutral-50/60 space-y-3" data-testid={`ship-actions-${s.shipment_id}`}>
      <div className="grid sm:grid-cols-3 gap-2 text-[11px] text-neutral-500">
        <div>Sub-order: <b className="text-neutral-700">{(s.vendor_suborder_id || "").slice(0, 10)}</b></div>
        <div>Carrier cost: <b className="text-neutral-700">{money(s.label_cost_cents || s.costs?.carrier_cost_cents)}</b></div>
        <div>Fulfillment loc: <b className="text-neutral-700">{s.fulfillment_location_id || "—"}</b></div>
      </div>

      {!labeled && buyable && s.status === "PENDING" && (
        <div className="bg-white border border-neutral-200 rounded-lg p-3">
          <div className="text-[11px] uppercase font-bold text-neutral-400 mb-2 flex items-center gap-1"><Package className="w-3.5 h-3.5" /> Package & Destination</div>
          <div className="grid grid-cols-2 sm:grid-cols-6 gap-2 mb-3">
            <input data-testid={`pkg-weight-${s.shipment_id}`} type="number" value={pkg.weight_grams} onChange={(e) => setPkg({ ...pkg, weight_grams: e.target.value })} placeholder="Weight (g)" className="binp" />
            <input data-testid={`pkg-length-${s.shipment_id}`} type="number" value={pkg.length} onChange={(e) => setPkg({ ...pkg, length: e.target.value })} placeholder="L" className="binp" />
            <input data-testid={`pkg-width-${s.shipment_id}`} type="number" value={pkg.width} onChange={(e) => setPkg({ ...pkg, width: e.target.value })} placeholder="W" className="binp" />
            <input data-testid={`pkg-height-${s.shipment_id}`} type="number" value={pkg.height} onChange={(e) => setPkg({ ...pkg, height: e.target.value })} placeholder="H" className="binp" />
            <input data-testid={`pkg-country-${s.shipment_id}`} value={pkg.country} onChange={(e) => setPkg({ ...pkg, country: e.target.value })} placeholder="Country" className="binp" />
            <input data-testid={`pkg-zip-${s.shipment_id}`} value={pkg.zip} onChange={(e) => setPkg({ ...pkg, zip: e.target.value })} placeholder="ZIP" className="binp" />
          </div>
          <Btn onClick={getRates} disabled={busy === "rates"} testid={`get-rates-${s.shipment_id}`} tone="ghost" icon={busy === "rates" ? Loader2 : DollarSign}>{busy === "rates" ? "Fetching…" : "Get Carrier Rates"}</Btn>
          {rates.length > 0 && (
            <div className="mt-3 grid sm:grid-cols-3 gap-2" data-testid={`rates-${s.shipment_id}`}>
              {rates.map((r) => (
                <button key={r.rate_id} onClick={() => act("buy", () => V.vShipBuy(s.shipment_id, r.rate_id), "Label purchased")} disabled={!!busy}
                  data-testid={`rate-${s.shipment_id}-${r.rate_id}`}
                  className="text-left border border-neutral-200 rounded-lg p-2.5 hover:border-taven-gold hover:bg-taven-gold/5 transition-colors disabled:opacity-40">
                  <div className="text-xs font-bold text-taven-black">{r.carrier} · {r.service_level}</div>
                  <div className="text-[11px] text-neutral-500">{money(r.cost_cents)} · {r.eta_days}d</div>
                  <div className="text-[10px] font-bold text-taven-orange mt-1">Buy label →</div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {!buyable && (
        <div className="text-[11px] text-neutral-500 bg-white border border-neutral-200 rounded-lg p-2.5">
          {s.fulfillment_responsibility === "SUPPLIER_FULFILLED" ? "Supplier provides tracking — no TAVEN label needed." :
            s.fulfillment_responsibility === "EXTERNAL_MARKETPLACE_FULFILLED" ? "External marketplace provides the label." : "This shipment is not label-buyable."}
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        {labeled && s.label_url && <a href={s.label_url} target="_blank" rel="noreferrer" data-testid={`print-${s.shipment_id}`}><Btn tone="dark" icon={Printer}>Print Label</Btn></a>}
        {labeled && <Btn tone="ghost" icon={Printer} testid={`reprint-${s.shipment_id}`} disabled={busy === "reprint"} onClick={() => act("reprint", async () => { const r = await V.vShipReprint(s.shipment_id); if (r.label_url) window.open(r.label_url, "_blank"); }, "Label reprinted")}>Reprint</Btn>}
        {labeled && <Btn tone="danger" icon={Ban} testid={`void-${s.shipment_id}`} disabled={busy === "void"} onClick={() => act("void", () => V.vShipVoid(s.shipment_id), "Label voided")}>Void</Btn>}
        {(s.label_status === "PURCHASED" || s.label_status === "VOIDED") && <Btn tone="danger" icon={Undo2} testid={`refund-${s.shipment_id}`} disabled={busy === "refund"} onClick={() => act("refund", () => V.vShipRefund(s.shipment_id), "Label refund requested")}>Refund</Btn>}
        {labeled && <Btn tone="ghost" icon={RotateCcw} testid={`return-label-${s.shipment_id}`} disabled={busy === "return"} onClick={() => act("return", () => V.vShipReturnLabel(s.shipment_id), "Return label created")}>Return Label</Btn>}
        {s.tracking_number && <Btn tone="ghost" icon={RefreshCw} testid={`sync-${s.shipment_id}`} disabled={busy === "sync"} onClick={() => act("sync", () => V.vShipSync(s.shipment_id), "Tracking synced")}>Sync Tracking</Btn>}
        {!s.fulfillment_hold
          ? <Btn tone="ghost" icon={PauseCircle} testid={`hold-${s.shipment_id}`} disabled={busy === "hold"} onClick={() => act("hold", () => V.vShipHold(s.shipment_id, true, "manual hold"), "Fulfillment hold placed")}>Place Hold</Btn>
          : <Btn tone="ghost" icon={PlayCircle} testid={`release-${s.shipment_id}`} disabled={busy === "hold"} onClick={() => act("hold", () => V.vShipHold(s.shipment_id, false), "Hold released")}>Release Hold</Btn>}
      </div>

      {Array.isArray(s.audit) && s.audit.length > 0 && (
        <details className="text-[11px] text-neutral-400" data-testid={`audit-${s.shipment_id}`}>
          <summary className="cursor-pointer">Tracking & audit history</summary>
          <div className="mt-2 space-y-0.5">{s.audit.slice(-8).map((a, i) => <div key={i}>· {a.a} {a.at ? `(${(a.at || "").slice(0, 16).replace("T", " ")})` : ""}</div>)}</div>
        </details>
      )}
      <style>{`.binp{border:1px solid #e5e5e5;border-radius:8px;padding:7px 9px;font-size:12px;width:100%}`}</style>
    </div>
  );
}

function Returns({ storeCtx }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const RET_FLOW = { REQUESTED: "APPROVED", APPROVED: "LABEL_ISSUED", LABEL_ISSUED: "IN_TRANSIT", IN_TRANSIT: "RECEIVED", RECEIVED: "REFUNDED" };
  const load = useCallback(async () => {
    setLoading(true);
    try { const d = await V.vShipReturns(scopeId(storeCtx)); setItems(d.returns || []); }
    catch { toast.error("Could not load returns"); } finally { setLoading(false); }
  }, [storeCtx]);
  useEffect(() => { load(); }, [load]);
  const advance = async (r) => {
    const to = RET_FLOW[r.status]; if (!to) return;
    try { await V.vShipReturnTransition(r.return_id, to); toast.success(`Return → ${to}`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <Card title="Returns" testid="returns-card">
      {loading ? <div className="py-6 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-taven-gold" /></div> :
        items.length === 0 ? <p className="text-sm text-neutral-400" data-testid="returns-empty">No returns yet.</p> :
          <div className="space-y-2" data-testid="returns-list">
            {items.map((r) => (
              <div key={r.return_id} className="flex items-center gap-3 border border-neutral-100 rounded-lg p-3" data-testid={`return-${r.return_id}`}>
                <div className="flex-1">
                  <div className="text-sm font-semibold text-taven-black">#{r.return_id.slice(0, 8)} · {r.order_type}</div>
                  <div className="text-[11px] text-neutral-400">{r.responsible_party} · cost {money(r.return_shipping_cost_cents)} · {r.refund_eligibility}</div>
                </div>
                <Badge cls="bg-neutral-100 text-neutral-600">{r.status}</Badge>
                {RET_FLOW[r.status] && <Btn tone="ghost" testid={`return-advance-${r.return_id}`} onClick={() => advance(r)}>{RET_FLOW[r.status]} →</Btn>}
              </div>
            ))}
          </div>}
    </Card>
  );
}

function Exceptions({ storeCtx }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try { const d = await V.vShipExceptions(scopeId(storeCtx)); setItems(d.exceptions || []); }
    catch { toast.error("Could not load exceptions"); } finally { setLoading(false); }
  }, [storeCtx]);
  useEffect(() => { load(); }, [load]);
  return (
    <Card title="Delivery & Fulfillment Exceptions" testid="exceptions-card"
      action={<button onClick={load} className="text-neutral-400 hover:text-taven-black" data-testid="exceptions-refresh"><RefreshCw className="w-4 h-4" /></button>}>
      {loading ? <div className="py-6 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-taven-gold" /></div> :
        items.length === 0 ? <div className="text-center py-8 text-neutral-400" data-testid="exceptions-empty"><CheckCircle2 className="w-7 h-7 mx-auto mb-2 text-green-500" /><p className="text-sm">No open exceptions. Everything is on track.</p></div> :
          <div className="space-y-2" data-testid="exceptions-list">
            {items.map((e) => (
              <div key={e.id} className="flex items-start gap-3 border border-red-100 bg-red-50/40 rounded-lg p-3" data-testid={`exception-${e.id}`}>
                <AlertTriangle className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-red-700">{(e.reason || "").replace(/_/g, " ")}</div>
                  <div className="text-[11px] text-neutral-500 break-all">{Object.entries(e.ref || {}).map(([k, v]) => `${k}: ${v}`).join(" · ")}</div>
                </div>
                <span className="text-[10px] text-neutral-400">{(e.created_at || "").slice(0, 10)}</span>
              </div>
            ))}
          </div>}
    </Card>
  );
}

function ProfileSettings({ store, stores }) {
  const target = store || stores?.[0];
  const [cfg, setCfg] = useState({ country: "US", zip: "", location_id: "", return_address: "", weight_grams: 250, length: 20, width: 15, height: 5 });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!target) { setLoading(false); return; }
    setLoading(true);
    V.vShipGetProfile(target.store_id).then((p) => {
      const o = p?.origin || {}; const dp = p?.default_package || {};
      setCfg({
        country: o.country || "US", zip: o.zip || "", location_id: o.location_id || "",
        return_address: p?.return_address || "",
        weight_grams: dp.weight_grams || 250, length: dp.dimensions?.length || 20,
        width: dp.dimensions?.width || 15, height: dp.dimensions?.height || 5,
      });
    }).catch(() => {}).finally(() => setLoading(false));
  }, [target?.store_id]);
  if (!target) return <Card testid="profile-no-store"><p className="text-sm text-neutral-500">Create a store first to configure a shipping origin.</p></Card>;
  if (loading) return <div className="py-8 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-taven-gold" /></div>;
  const save = async () => {
    setBusy(true);
    try {
      await V.vShipSetProfile(target.store_id, {
        origin: { country: cfg.country, zip: cfg.zip, location_id: cfg.location_id },
        return_address: cfg.return_address,
        default_package: { weight_grams: Number(cfg.weight_grams), dimensions: { length: Number(cfg.length), width: Number(cfg.width), height: Number(cfg.height) } },
      });
      toast.success("Shipping profile saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };
  const F = ({ label, children }) => <label className="block mb-3"><span className="block text-[11px] uppercase tracking-wide text-neutral-400 font-semibold mb-1">{label}</span>{children}</label>;
  return (
    <div>
      <Card title={`Shipping Origin — ${target.name}`} testid="profile-origin-card">
        <p className="text-[11px] text-neutral-400 mb-3 flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> The fulfillment location + origin used to fetch carrier rates and buy labels.</p>
        <div className="grid sm:grid-cols-3 gap-3">
          <F label="Origin country"><input data-testid="pf-country" value={cfg.country} onChange={(e) => setCfg({ ...cfg, country: e.target.value })} className="pinp" /></F>
          <F label="Origin ZIP"><input data-testid="pf-zip" value={cfg.zip} onChange={(e) => setCfg({ ...cfg, zip: e.target.value })} className="pinp" /></F>
          <F label="Shopify location ID"><input data-testid="pf-location" value={cfg.location_id} onChange={(e) => setCfg({ ...cfg, location_id: e.target.value })} className="pinp" /></F>
        </div>
        <F label="Return address"><input data-testid="pf-return" value={cfg.return_address} onChange={(e) => setCfg({ ...cfg, return_address: e.target.value })} className="pinp" /></F>
      </Card>
      <Card title="Default Package" testid="profile-package-card">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <F label="Weight (g)"><input data-testid="pf-weight" type="number" value={cfg.weight_grams} onChange={(e) => setCfg({ ...cfg, weight_grams: e.target.value })} className="pinp" /></F>
          <F label="Length (cm)"><input data-testid="pf-length" type="number" value={cfg.length} onChange={(e) => setCfg({ ...cfg, length: e.target.value })} className="pinp" /></F>
          <F label="Width (cm)"><input data-testid="pf-width" type="number" value={cfg.width} onChange={(e) => setCfg({ ...cfg, width: e.target.value })} className="pinp" /></F>
          <F label="Height (cm)"><input data-testid="pf-height" type="number" value={cfg.height} onChange={(e) => setCfg({ ...cfg, height: e.target.value })} className="pinp" /></F>
        </div>
        <Btn onClick={save} disabled={busy} testid="save-profile-btn" icon={busy ? Loader2 : CheckCircle2}>{busy ? "Saving…" : "Save Shipping Profile"}</Btn>
      </Card>
      <style>{`.pinp{width:100%;border:1px solid #e5e5e5;border-radius:8px;padding:8px 10px;font-size:13px}`}</style>
    </div>
  );
}
